import { api } from '@xtnd/web/shared/api';
import type {
    CreateUploadPresignedUrlPayload,
    UploadPresignedUrlResponse,
} from '../types';

/**
 * Service class for managing file uploads.
 * Provides functionality to generate S3 presigned URLs for secure file uploads.
 *
 * @class UploadService
 * @example
 * ```typescript
 * import { uploadApi } from './api/uploadApi';
 *
 * // Create a presigned URL for file upload
 * const result = await uploadApi.createPresignedUrl({
 *   fileName: 'document.pdf',
 *   contentType: 'application/pdf'
 * });
 *
 * // Use the presigned URL to upload the file
 * await fetch(result.data.uploadUrl, {
 *   method: 'PUT',
 *   body: file,
 *   headers: { 'Content-Type': result.data.contentType }
 * });
 * ```
 */
class UploadService {
    /**
     * Base resource path for upload endpoints.
     *
     * @private
     * @readonly
     * @type {string}
     */
    private readonly baseResource = '/uploads';

    /**
     * Creates a presigned URL for uploading a file to S3.
     * The presigned URL allows temporary, secure upload access without exposing AWS credentials.
     *
     * @param {CreateUploadPresignedUrlPayload} payload - File upload parameters
     * @param {string} payload.fileName - Original file name to be uploaded
     * @param {string} [payload.contentType] - Optional MIME type of the file
     * @returns {Promise<UploadPresignedUrlResponse>} Promise resolving to the presigned URL and S3 key
     * @throws {Error} If the API request fails or returns an error
     *
     * @example
     * ```typescript
     * const result = await uploadApi.createPresignedUrl({
     *   fileName: 'invoice.pdf',
     *   contentType: 'application/pdf'
     * });
     *
     * console.log('Upload URL:', result.data.uploadUrl);
     * console.log('S3 Key:', result.data.key);
     * ```
     */
    async createPresignedUrl(
        payload: CreateUploadPresignedUrlPayload
    ): Promise<UploadPresignedUrlResponse> {
        const endpoint = `${this.baseResource}/presign`;

        const response = await api.post<UploadPresignedUrlResponse>(endpoint, payload);

        return response;
    }
}

/**
 * Singleton instance of UploadService for application-wide use.
 * Provides a shared service instance for managing file uploads across the application.
 *
 * @constant
 * @type {UploadService}
 *
 * @example
 * ```typescript
 * import { uploadApi } from './api/uploadApi';
 *
 * // Use the singleton instance
 * const result = await uploadApi.createPresignedUrl({
 *   fileName: 'document.pdf',
 *   contentType: 'application/pdf'
 * });
 * ```
 */
export const uploadApi = new UploadService();
